import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  intensityLevels,
  type DepartmentView,
  type DrillView,
  type PlanVersionView,
  type SupportedLocale,
  type TrainingPlanView,
} from '@stm/contracts';
import { translate } from '@stm/i18n';
import { TaskSection } from './TaskSection';
import { ApiRequestError, apiRequest } from './api';
import {
  activeSeconds,
  availableSeconds,
  canSubmitDrill,
  canSubmitPlan,
  drillFormOf,
  drillRequestBody,
  drillTitle,
  emptyBlockForm,
  emptyDrillForm,
  emptyPlanForm,
  intensityKeys,
  minutesLabel,
  moveBlock,
  planFitsForm,
  planFormOf,
  planRequestBody,
  planStateKeys,
  wholeClub,
  type BlockForm,
  type DrillForm,
  type PlanForm,
} from './training';

export interface TrainingPanelProps {
  locale: SupportedLocale;
  organizationId: string;
  csrfToken: string;
  /** Whether the person may create and change drills and plans; the server enforces it as well. */
  canManage: boolean;
}

type ErrorKey = ApiRequestError['messageKey'];

function errorKeyOf(error: unknown): ErrorKey {
  return error instanceof ApiRequestError ? error.messageKey : 'error.unexpected';
}

interface DrillsSectionProps {
  locale: SupportedLocale;
  base: string;
  csrfToken: string;
  canManage: boolean;
  departments: readonly DepartmentView[];
  drills: readonly DrillView[] | null;
  onChanged: () => Promise<void>;
}

function DrillsSection({
  locale,
  base,
  csrfToken,
  canManage,
  departments,
  drills,
  onChanged,
}: DrillsSectionProps): React.JSX.Element {
  const [form, setForm] = useState<DrillForm>(() => emptyDrillForm(departments[0]?.id ?? ''));
  const [editing, setEditing] = useState<DrillView | null>(null);
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);

  // Until the person chooses, a new drill belongs to the first department they may work in.
  const departmentChoice =
    form.departmentId === '' ? (departments[0]?.id ?? wholeClub) : form.departmentId;

  async function submit(): Promise<void> {
    setErrorKey(null);
    try {
      if (editing === null) {
        await apiRequest<DrillView>(`${base}/drills`, {
          method: 'POST',
          body: drillRequestBody({ ...form, departmentId: departmentChoice }, locale),
          csrfToken,
        });
      } else {
        // A new version carries the content only; the department of a drill never changes.
        const { titles, protocol, instructions, durationSeconds, equipment } = drillRequestBody(
          form,
          locale,
          editing.titles,
        );
        const content = { titles, protocol, instructions, durationSeconds, equipment };
        await apiRequest<DrillView>(`${base}/drills/${editing.id}/versions`, {
          method: 'POST',
          body: content,
          csrfToken,
        });
      }
      setEditing(null);
      setForm(emptyDrillForm(departments[0]?.id ?? ''));
      await onChanged();
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }

  return (
    <Box component="section" aria-label={translate(locale, 'training.drills.title')}>
      <Typography variant="h5" component="h2" gutterBottom>
        {translate(locale, 'training.drills.title')}
      </Typography>
      {errorKey !== null && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      {drills?.length === 0 && (
        <Typography color="text.secondary">{translate(locale, 'training.drills.empty')}</Typography>
      )}
      <Stack component="ul" spacing={1} sx={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {drills?.map((drill) => (
          <Box
            component="li"
            key={drill.id}
            sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}
          >
            <Typography sx={{ fontWeight: 500 }}>{drillTitle(drill.titles, locale)}</Typography>
            <Chip
              size="small"
              label={`${minutesLabel(drill.durationSeconds)} ${translate(locale, 'training.plan.minutes')}`}
            />
            <Chip
              size="small"
              variant="outlined"
              label={`${translate(locale, 'training.drill.version')} ${String(drill.versionNumber)}`}
            />
            {drill.equipment.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                {drill.equipment.join(', ')}
              </Typography>
            )}
            {canManage && (
              <Button
                size="small"
                onClick={() => {
                  setEditing(drill);
                  setForm(drillFormOf(drill, locale));
                }}
              >
                {translate(locale, 'training.drill.edit')}
              </Button>
            )}
          </Box>
        ))}
      </Stack>
      {canManage && (
        <Box
          component="form"
          sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          {editing === null && (
            <TextField
              select
              size="small"
              sx={{ minWidth: 180 }}
              label={translate(locale, 'training.drill.department')}
              value={departmentChoice}
              onChange={(event) => {
                setForm({ ...form, departmentId: event.target.value });
              }}
            >
              <MenuItem value={wholeClub}>{translate(locale, 'training.drill.wholeClub')}</MenuItem>
              {departments.map((department) => (
                <MenuItem key={department.id} value={department.id}>
                  {department.name}
                </MenuItem>
              ))}
            </TextField>
          )}
          <TextField
            size="small"
            required
            sx={{ minWidth: 220 }}
            label={translate(locale, 'training.drill.title')}
            value={form.title}
            onChange={(event) => {
              setForm({ ...form, title: event.target.value });
            }}
          />
          <TextField
            size="small"
            sx={{ width: 150 }}
            label={translate(locale, 'training.drill.minutes')}
            value={form.minutes}
            onChange={(event) => {
              setForm({ ...form, minutes: event.target.value });
            }}
            slotProps={{ htmlInput: { inputMode: 'numeric' } }}
          />
          <TextField
            size="small"
            sx={{ minWidth: 220 }}
            label={translate(locale, 'training.drill.equipment')}
            value={form.equipment}
            onChange={(event) => {
              setForm({ ...form, equipment: event.target.value });
            }}
          />
          <TextField
            size="small"
            multiline
            sx={{ minWidth: 300, flexGrow: 1 }}
            label={translate(locale, 'training.drill.instructions')}
            value={form.instructions}
            onChange={(event) => {
              setForm({ ...form, instructions: event.target.value });
            }}
          />
          <TextField
            size="small"
            sx={{ minWidth: 220 }}
            label={translate(locale, 'training.drill.protocol')}
            value={form.protocol}
            onChange={(event) => {
              setForm({ ...form, protocol: event.target.value });
            }}
          />
          <Button
            type="submit"
            variant="contained"
            sx={{ minHeight: 40 }}
            disabled={!canSubmitDrill(form)}
          >
            {translate(
              locale,
              editing === null ? 'training.drill.add' : 'training.drill.newVersion',
            )}
          </Button>
        </Box>
      )}
    </Box>
  );
}

interface BlockEditorProps {
  locale: SupportedLocale;
  index: number;
  count: number;
  block: BlockForm;
  drills: readonly DrillView[];
  readOnly: boolean;
  onChange: (block: BlockForm) => void;
  onMove: (offset: -1 | 1) => void;
  onRemove: () => void;
}

function BlockEditor({
  locale,
  index,
  count,
  block,
  drills,
  readOnly,
  onChange,
  onMove,
  onRemove,
}: BlockEditorProps): React.JSX.Element {
  return (
    <Paper variant="outlined" sx={{ padding: 1.5 }}>
      <Typography variant="subtitle2" component="h4" gutterBottom>
        {index + 1}.
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          required
          disabled={readOnly}
          sx={{ minWidth: 240, flexGrow: 1 }}
          label={translate(locale, 'training.block.objective')}
          value={block.objective}
          onChange={(event) => {
            onChange({ ...block, objective: event.target.value });
          }}
        />
        <TextField
          size="small"
          disabled={readOnly || block.stations.length > 0}
          sx={{ width: 110 }}
          label={translate(locale, 'training.block.minutes')}
          value={block.stations.length > 0 ? '' : block.minutes}
          onChange={(event) => {
            onChange({ ...block, minutes: event.target.value });
          }}
          slotProps={{ htmlInput: { inputMode: 'numeric' } }}
        />
        <TextField
          select
          size="small"
          disabled={readOnly}
          sx={{ minWidth: 200 }}
          label={translate(locale, 'training.block.drill')}
          value={block.drillVersionId}
          onChange={(event) => {
            onChange({ ...block, drillVersionId: event.target.value });
          }}
        >
          <MenuItem value="">{translate(locale, 'training.block.noDrill')}</MenuItem>
          {drills.map((drill) => (
            <MenuItem key={drill.versionId} value={drill.versionId}>
              {drillTitle(drill.titles, locale)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          disabled={readOnly}
          sx={{ minWidth: 140 }}
          label={translate(locale, 'training.block.intensity')}
          value={block.intensity}
          onChange={(event) => {
            onChange({ ...block, intensity: event.target.value as BlockForm['intensity'] });
          }}
        >
          <MenuItem value="">—</MenuItem>
          {intensityLevels.map((level) => (
            <MenuItem key={level} value={level}>
              {translate(locale, intensityKeys[level])}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          disabled={readOnly}
          sx={{ minWidth: 180 }}
          label={translate(locale, 'training.block.equipment')}
          value={block.equipment}
          onChange={(event) => {
            onChange({ ...block, equipment: event.target.value });
          }}
        />
        <TextField
          size="small"
          disabled={readOnly}
          sx={{ minWidth: 140 }}
          label={translate(locale, 'training.block.surface')}
          value={block.surface}
          onChange={(event) => {
            onChange({ ...block, surface: event.target.value });
          }}
        />
      </Box>
      <TextField
        size="small"
        fullWidth
        multiline
        disabled={readOnly}
        sx={{ mt: 1 }}
        label={translate(locale, 'training.block.notes')}
        value={block.coachNotes}
        onChange={(event) => {
          onChange({ ...block, coachNotes: event.target.value });
        }}
      />
      <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControlLabel
          label={translate(locale, 'training.block.familyVisible')}
          control={
            <Checkbox
              disabled={readOnly}
              checked={block.familyVisible}
              onChange={(event) => {
                onChange({ ...block, familyVisible: event.target.checked });
              }}
            />
          }
        />
        {block.familyVisible && (
          <TextField
            size="small"
            required
            disabled={readOnly}
            sx={{ minWidth: 260, flexGrow: 1 }}
            label={translate(locale, 'training.block.familySummary')}
            value={block.familySummary}
            onChange={(event) => {
              onChange({ ...block, familySummary: event.target.value });
            }}
          />
        )}
      </Box>
      {block.stations.length > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {translate(locale, 'training.block.stationsNote')}
        </Typography>
      )}
      <Stack spacing={1} sx={{ mt: 1 }}>
        {block.stations.map((station, stationIndex) => (
          <Box
            key={stationIndex}
            sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}
          >
            <TextField
              size="small"
              required
              disabled={readOnly}
              label={translate(locale, 'training.station.label')}
              value={station.label}
              onChange={(event) => {
                onChange({
                  ...block,
                  stations: block.stations.map((candidate, position) =>
                    position === stationIndex
                      ? { ...candidate, label: event.target.value }
                      : candidate,
                  ),
                });
              }}
            />
            <TextField
              size="small"
              disabled={readOnly}
              sx={{ width: 110 }}
              label={translate(locale, 'training.block.minutes')}
              value={station.minutes}
              onChange={(event) => {
                onChange({
                  ...block,
                  stations: block.stations.map((candidate, position) =>
                    position === stationIndex
                      ? { ...candidate, minutes: event.target.value }
                      : candidate,
                  ),
                });
              }}
              slotProps={{ htmlInput: { inputMode: 'numeric' } }}
            />
            {!readOnly && (
              <Button
                size="small"
                onClick={() => {
                  onChange({
                    ...block,
                    stations: block.stations.filter(
                      (_candidate, position) => position !== stationIndex,
                    ),
                  });
                }}
              >
                {translate(locale, 'training.station.remove')}
              </Button>
            )}
          </Box>
        ))}
      </Stack>
      {!readOnly && (
        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
          <Button
            size="small"
            onClick={() => {
              onChange({
                ...block,
                stations: [...block.stations, { label: '', minutes: block.minutes }],
              });
            }}
          >
            {translate(locale, 'training.station.add')}
          </Button>
          <Button
            size="small"
            disabled={index === 0}
            onClick={() => {
              onMove(-1);
            }}
          >
            {translate(locale, 'training.block.up')}
          </Button>
          <Button
            size="small"
            disabled={index === count - 1}
            onClick={() => {
              onMove(1);
            }}
          >
            {translate(locale, 'training.block.down')}
          </Button>
          <Button size="small" color="error" disabled={count === 1} onClick={onRemove}>
            {translate(locale, 'training.block.remove')}
          </Button>
        </Stack>
      )}
    </Paper>
  );
}

interface PlanEditorProps {
  locale: SupportedLocale;
  base: string;
  csrfToken: string;
  departments: readonly DepartmentView[];
  drills: readonly DrillView[];
  /** The version being edited, or null for a new plan. */
  version: PlanVersionView | null;
  canManage: boolean;
  onSaved: (versionId: string) => Promise<void>;
  onClose: () => void;
}

function PlanEditor({
  locale,
  base,
  csrfToken,
  departments,
  drills,
  version,
  canManage,
  onSaved,
  onClose,
}: PlanEditorProps): React.JSX.Element {
  const [form, setForm] = useState<PlanForm>(() =>
    version === null ? emptyPlanForm(departments[0]?.id ?? '') : planFormOf(version),
  );
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);
  const [saved, setSaved] = useState(false);
  const readOnly = !canManage || (version !== null && version.publicationState !== 'draft');
  const fits = planFitsForm(form);

  async function run(work: () => Promise<PlanVersionView>): Promise<void> {
    setErrorKey(null);
    setSaved(false);
    try {
      const result = await work();
      setSaved(true);
      await onSaved(result.versionId);
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }

  const save = (): Promise<void> =>
    run(() =>
      version === null
        ? apiRequest<PlanVersionView>(`${base}/training-plans`, {
            method: 'POST',
            body: { departmentId: form.departmentId, ...planRequestBody(form) },
            csrfToken,
          })
        : apiRequest<PlanVersionView>(`${base}/training-plans/versions/${version.versionId}`, {
            method: 'PUT',
            body: planRequestBody(form),
            csrfToken,
            ifMatchVersion: version.revision,
          }),
    );

  const publish = (): Promise<void> =>
    run(async () => {
      if (version === null) throw new Error('A plan must be saved before it is published.');
      // Publish what is on screen: save first so the frozen version is exactly this.
      const current = await apiRequest<PlanVersionView>(
        `${base}/training-plans/versions/${version.versionId}`,
        { method: 'PUT', body: planRequestBody(form), csrfToken, ifMatchVersion: version.revision },
      );
      return await apiRequest<PlanVersionView>(
        `${base}/training-plans/versions/${version.versionId}/publish`,
        { method: 'POST', body: {}, csrfToken, ifMatchVersion: current.revision },
      );
    });

  const newDraft = (): Promise<void> =>
    run(async () => {
      if (version === null) throw new Error('A plan must be saved before it is copied.');
      return await apiRequest<PlanVersionView>(
        `${base}/training-plans/${version.planId}/versions`,
        {
          method: 'POST',
          body: {},
          csrfToken,
        },
      );
    });

  const updateBlock = (index: number, block: BlockForm): void => {
    setForm({
      ...form,
      blocks: form.blocks.map((candidate, position) => (position === index ? block : candidate)),
    });
  };

  return (
    <Paper variant="outlined" sx={{ padding: 2, mt: 1 }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
        <Typography variant="h6" component="h3">
          {version === null ? translate(locale, 'training.plan.new') : version.title}
        </Typography>
        {version !== null && (
          <Chip size="small" label={translate(locale, planStateKeys[version.publicationState])} />
        )}
        <Button size="small" sx={{ marginLeft: 'auto' }} onClick={onClose}>
          {translate(locale, 'training.plan.close')}
        </Button>
      </Box>
      {readOnly && version !== null && (
        <Alert severity="info" sx={{ mb: 1 }}>
          {translate(locale, 'training.action.frozen')}
        </Alert>
      )}
      {errorKey !== null && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      {saved && errorKey === null && (
        <Alert severity="success" sx={{ mb: 1 }}>
          {translate(locale, 'training.saved')}
        </Alert>
      )}
      <Box
        component="form"
        onSubmit={(event) => {
          event.preventDefault();
          if (!readOnly) void save();
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {version === null && (
            <TextField
              select
              size="small"
              sx={{ minWidth: 180 }}
              label={translate(locale, 'calendar.filter.department')}
              value={form.departmentId}
              onChange={(event) => {
                setForm({ ...form, departmentId: event.target.value });
              }}
            >
              {departments.map((department) => (
                <MenuItem key={department.id} value={department.id}>
                  {department.name}
                </MenuItem>
              ))}
            </TextField>
          )}
          <TextField
            size="small"
            required
            disabled={readOnly}
            sx={{ minWidth: 240, flexGrow: 1 }}
            label={translate(locale, 'training.plan.title')}
            value={form.title}
            onChange={(event) => {
              setForm({ ...form, title: event.target.value });
            }}
          />
          <TextField
            size="small"
            disabled={readOnly}
            sx={{ minWidth: 240, flexGrow: 1 }}
            label={translate(locale, 'training.plan.objective')}
            value={form.objective}
            onChange={(event) => {
              setForm({ ...form, objective: event.target.value });
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
          {(
            [
              ['sessionMinutes', 'training.plan.sessionMinutes'],
              ['preparationMinutes', 'training.plan.prepMinutes'],
              ['breakMinutes', 'training.plan.breakMinutes'],
            ] as const
          ).map(([field, labelKey]) => (
            <TextField
              key={field}
              size="small"
              disabled={readOnly}
              sx={{ width: 190 }}
              label={translate(locale, labelKey)}
              value={form[field]}
              onChange={(event) => {
                setForm({ ...form, [field]: event.target.value });
              }}
              slotProps={{ htmlInput: { inputMode: 'numeric' } }}
            />
          ))}
        </Box>
        <Alert severity={fits ? 'success' : 'warning'} sx={{ mt: 1 }}>
          {translate(locale, 'training.plan.active')}: {minutesLabel(activeSeconds(form))}{' '}
          {translate(locale, 'training.plan.minutes')} / {minutesLabel(availableSeconds(form))}{' '}
          {translate(locale, 'training.plan.minutes')}{' '}
          {translate(locale, 'training.plan.available')}.{' '}
          {translate(locale, fits ? 'training.plan.fits' : 'training.plan.doesNotFit')}
        </Alert>
        {!fits && (
          <TextField
            size="small"
            fullWidth
            disabled={readOnly}
            sx={{ mt: 1 }}
            label={translate(locale, 'training.plan.override')}
            value={form.overrideReason}
            onChange={(event) => {
              setForm({ ...form, overrideReason: event.target.value });
            }}
          />
        )}
        <Typography variant="subtitle1" component="h4" sx={{ mt: 2 }} gutterBottom>
          {translate(locale, 'training.plan.blocks')}
        </Typography>
        <Stack spacing={1.5}>
          {form.blocks.map((block, index) => (
            <BlockEditor
              key={index}
              locale={locale}
              index={index}
              count={form.blocks.length}
              block={block}
              drills={drills}
              readOnly={readOnly}
              onChange={(changed) => {
                updateBlock(index, changed);
              }}
              onMove={(offset) => {
                setForm({ ...form, blocks: moveBlock(form.blocks, index, offset) });
              }}
              onRemove={() => {
                setForm({
                  ...form,
                  blocks: form.blocks.filter((_candidate, position) => position !== index),
                });
              }}
            />
          ))}
        </Stack>
        {!readOnly && (
          <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
            <Button
              onClick={() => {
                setForm({ ...form, blocks: [...form.blocks, emptyBlockForm()] });
              }}
            >
              {translate(locale, 'training.block.add')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{ minHeight: 44 }}
              disabled={!canSubmitPlan(form)}
            >
              {translate(
                locale,
                version === null ? 'training.action.create' : 'training.action.save',
              )}
            </Button>
            {version !== null && (
              <Button
                variant="outlined"
                sx={{ minHeight: 44 }}
                disabled={!canSubmitPlan(form)}
                onClick={() => {
                  void publish();
                }}
              >
                {translate(locale, 'training.action.publish')}
              </Button>
            )}
          </Stack>
        )}
      </Box>
      {canManage && version !== null && version.publicationState !== 'draft' && (
        <Button
          variant="outlined"
          sx={{ mt: 2, minHeight: 44 }}
          onClick={() => {
            void newDraft();
          }}
        >
          {translate(locale, 'training.action.newDraft')}
        </Button>
      )}
    </Paper>
  );
}

/** The Training page: drills and versioned training plans for the departments the person coaches. */
export function TrainingPanel({
  locale,
  organizationId,
  csrfToken,
  canManage,
}: TrainingPanelProps): React.JSX.Element {
  const base = `/api/v1/organizations/${organizationId}`;
  const [departments, setDepartments] = useState<DepartmentView[]>([]);
  const [drills, setDrills] = useState<DrillView[] | null>(null);
  const [plans, setPlans] = useState<TrainingPlanView[] | null>(null);
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);
  const [open, setOpen] = useState<PlanVersionView | 'new' | null>(null);
  // Changing the key rebuilds the editor, so it shows the version that was just saved or created.
  const [editorKey, setEditorKey] = useState(0);

  const load = useCallback(async () => {
    try {
      const [loadedDrills, loadedPlans] = await Promise.all([
        apiRequest<DrillView[]>(`${base}/drills`),
        apiRequest<TrainingPlanView[]>(`${base}/training-plans`),
      ]);
      setDrills(loadedDrills);
      setPlans(loadedPlans);
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }, [base]);

  useEffect(() => {
    apiRequest<DepartmentView[]>(`${base}/departments`).then(setDepartments, () => {
      setDepartments([]);
    });
    void load();
  }, [base, load]);

  async function openVersion(versionId: string): Promise<void> {
    try {
      const version = await apiRequest<PlanVersionView>(
        `${base}/training-plans/versions/${versionId}`,
      );
      setOpen(version);
      setEditorKey((current) => current + 1);
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }

  return (
    <Box sx={{ mt: 2 }}>
      {errorKey !== null && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      <TaskSection
        locale={locale}
        titleKey="training.drills.title"
        descriptionKey="task.drills.description"
      >
        <DrillsSection
          locale={locale}
          base={base}
          csrfToken={csrfToken}
          canManage={canManage}
          departments={departments}
          drills={drills}
          onChanged={load}
        />
      </TaskSection>
      <Box
        component="section"
        aria-label={translate(locale, 'training.plans.title')}
        sx={{ mt: 4 }}
      >
        <Typography variant="h5" component="h2" gutterBottom>
          {translate(locale, 'training.plans.title')}
        </Typography>
        {plans?.length === 0 && (
          <Typography color="text.secondary">
            {translate(locale, 'training.plans.empty')}
          </Typography>
        )}
        <Stack spacing={1}>
          {plans?.map((plan) => (
            <Paper key={plan.planId} variant="outlined" sx={{ padding: 1.5 }}>
              <Typography sx={{ fontWeight: 600 }}>{plan.title}</Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 1 }}>
                {plan.versions.map((version) => (
                  <Button
                    key={version.versionId}
                    size="small"
                    variant="outlined"
                    sx={{ minHeight: 40 }}
                    onClick={() => {
                      void openVersion(version.versionId);
                    }}
                  >
                    {translate(locale, 'training.drill.version')} {version.versionNumber} ·{' '}
                    {translate(locale, planStateKeys[version.publicationState])}
                  </Button>
                ))}
              </Stack>
            </Paper>
          ))}
        </Stack>
        {canManage && open === null && (
          <Button
            variant="contained"
            sx={{ mt: 2, minHeight: 44 }}
            onClick={() => {
              setOpen('new');
              setEditorKey((current) => current + 1);
            }}
          >
            {translate(locale, 'training.plan.new')}
          </Button>
        )}
        {open !== null && (
          <PlanEditor
            key={editorKey}
            locale={locale}
            base={base}
            csrfToken={csrfToken}
            departments={departments}
            drills={drills ?? []}
            version={open === 'new' ? null : open}
            canManage={canManage}
            onSaved={async (versionId) => {
              await load();
              await openVersion(versionId);
            }}
            onClose={() => {
              setOpen(null);
            }}
          />
        )}
      </Box>
    </Box>
  );
}
